import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProviderService } from 'src/app/services/provider.service';
import { UtilsService } from 'src/app/services/utils.service';

type StatusFilter = 'all' | 'pending' | 'authorized' | 'paid';

interface MonthlyCard {
    id: number;
    provider_id: number;
    provider_name: string;
    concept: string;
    period: string;
    amount: number;
    due_date: string | null;
    status: number;
    has_invoice: boolean;
    avatarBg: string;
    avatarColor: string;
    avatarInitial: string;
}

interface PaymentDetail extends MonthlyCard {
    invoice_url: string | null;
    invoice_number: string | null;
    invoice_date: string | null;
    authorized_at: string | null;
    paid_at: string | null;
    payment_proof_url: string | null;
    recurrence: string | null;
    provider: any;
}

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

@Component({
    selector: 'app-provider-monthly',
    templateUrl: './provider-monthly.component.html',
    styleUrls: ['./provider-monthly.component.css']
})
export class ProviderMonthlyComponent implements OnInit {
    constructor(
        private providerService: ProviderService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private el: ElementRef<HTMLElement>,
    ) { }

    // Misma paleta que provider-list/provider-detail, para que el color del
    // avatar de un proveedor sea consistente en toda la app.
    private readonly avatarPalette = [
        { bg: '#eef2ff', color: '#4f46e5' },
        { bg: '#e6fbf3', color: '#10b981' },
        { bg: '#fffbeb', color: '#d97706' },
        { bg: '#e6f0ff', color: '#0066ff' },
        { bg: '#fdf2f8', color: '#db2777' },
        { bg: '#f5f3ff', color: '#8b5cf6' },
        { bg: '#fff7ed', color: '#ea580c' },
        { bg: '#fee2e2', color: '#ef4444' },
    ];

    period = this.currentPeriod();
    monthPickerOpen = false;
    pickerYear = Number(this.period.split('-')[0]);
    readonly monthShortNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    summary: any = null;
    statusFilter: StatusFilter = 'all';
    search = '';

    cards: MonthlyCard[] = [];
    page = 1;
    perPage = 6;
    total = 0;
    hasMore = false;
    loadingMore = false;

    ngOnInit(): void {
        this.loadSummary();
        this.loadCards(true);
    }

    private currentPeriod(): string {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }

    get periodLabel(): string {
        const [y, m] = this.period.split('-').map(Number);
        return `${MONTH_NAMES[(m || 1) - 1]} ${y}`;
    }

    onPeriodChange(value: string): void {
        if (!value) return;
        this.period = value;
        this.loadSummary();
        this.loadCards(true);
    }

    toggleMonthPicker(): void {
        if (!this.monthPickerOpen) {
            this.pickerYear = Number(this.period.split('-')[0]);
        }
        this.monthPickerOpen = !this.monthPickerOpen;
    }

    pickerPrevYear(): void {
        this.pickerYear--;
    }

    pickerNextYear(): void {
        this.pickerYear++;
    }

    isPickerMonthSelected(monthIndex: number): boolean {
        const [y, m] = this.period.split('-').map(Number);
        return y === this.pickerYear && m === monthIndex + 1;
    }

    selectPickerMonth(monthIndex: number): void {
        this.monthPickerOpen = false;
        this.onPeriodChange(`${this.pickerYear}-${String(monthIndex + 1).padStart(2, '0')}`);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.monthPickerOpen) return;
        const picker = this.el.nativeElement.querySelector('.monthly-month-picker');
        if (picker && !picker.contains(event.target as Node)) {
            this.monthPickerOpen = false;
        }
    }

    loadSummary(): void {
        this.providerService.getMonthlySummary(this.period).subscribe({
            next: (resp: any) => this.summary = resp,
            error: () => this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el resumen mensual', icon: 'error' })
        });
    }

    setStatusFilter(filter: StatusFilter): void {
        if (this.statusFilter === filter) return;
        this.statusFilter = filter;
        this.loadCards(true);
    }

    onSearchChange(): void {
        this.loadCards(true);
    }

    private apiStatus(): string | undefined {
        return this.statusFilter === 'all' ? undefined : this.statusFilter;
    }

    loadCards(reset: boolean): void {
        if (reset) {
            this.page = 1;
            this.spinner.show();
        } else {
            this.loadingMore = true;
        }

        this.providerService.getMonthlyPayments({
            period: this.period,
            status: this.apiStatus(),
            search: this.search || undefined,
            page: this.page,
            per_page: this.perPage,
        }).subscribe({
            next: (resp: any) => {
                const rows = (resp?.data || []).map((r: any) => this.toCard(r));
                this.cards = reset ? rows : [...this.cards, ...rows];
                this.total = resp?.total || 0;
                this.hasMore = !!resp?.has_more;
                this.spinner.hide();
                this.loadingMore = false;
            },
            error: () => {
                this.spinner.hide();
                this.loadingMore = false;
                this.utils.alertSwal({ title: 'Error', text: 'No se pudieron cargar los pagos', icon: 'error' });
            }
        });
    }

    loadMore(): void {
        this.page++;
        this.loadCards(false);
    }

    // ---- Panel lateral "Ver detalle" ----

    detailPayment: PaymentDetail | null = null;
    detailLoading = false;
    uploadingInvoice = false;
    actionLoading = false;
    invoiceNumberDraft = '';
    invoiceDateDraft = '';

    openDetail(card: MonthlyCard): void {
        this.detailPayment = null;
        this.detailLoading = true;
        this.providerService.getPaymentDetail(card.id).subscribe({
            next: (resp: any) => {
                this.detailPayment = this.toCard(resp.data) as PaymentDetail;
                this.syncInvoiceDrafts();
                this.detailLoading = false;
            },
            error: () => {
                this.detailLoading = false;
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el detalle del pago', icon: 'error' });
            }
        });
    }

    private syncInvoiceDrafts(): void {
        this.invoiceNumberDraft = this.detailPayment?.invoice_number || '';
        this.invoiceDateDraft = this.detailPayment?.invoice_date || '';
    }

    closeDetail(): void {
        this.detailPayment = null;
    }

    /** Refresca el panel con la respuesta de la acción y recarga el
     * listado/KPIs, ya que una acción puede mover el pago de bucket. */
    private refreshAfterAction(updated: any): void {
        this.detailPayment = this.toCard(updated) as PaymentDetail;
        this.syncInvoiceDrafts();
        this.loadSummary();
        this.loadCards(true);
    }

    onInvoiceSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file || !this.detailPayment) return;

        this.uploadingInvoice = true;
        this.providerService.uploadPaymentInvoice(this.detailPayment.id, file).subscribe({
            next: (resp: any) => {
                this.uploadingInvoice = false;
                this.refreshAfterAction(resp.data);
            },
            error: (err: any) => {
                this.uploadingInvoice = false;
                this.utils.alertSwal({ title: 'Error', text: typeof err === 'string' ? err : 'No se pudo cargar la factura', icon: 'error' });
            }
        });
        input.value = '';
    }

    markAuthorized(): void {
        if (!this.detailPayment) return;
        this.actionLoading = true;
        this.providerService.authorizePayment(this.detailPayment.id).subscribe({
            next: (resp: any) => {
                this.actionLoading = false;
                this.refreshAfterAction(resp.data);
            },
            error: (err: any) => {
                this.actionLoading = false;
                this.utils.alertSwal({ title: 'Error', text: typeof err === 'string' ? err : 'No se pudo autorizar el pago', icon: 'error' });
            }
        });
    }

    onPaymentProofSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file || !this.detailPayment) return;

        this.actionLoading = true;
        this.providerService.registerPayment(this.detailPayment.id, file).subscribe({
            next: (resp: any) => {
                this.actionLoading = false;
                this.refreshAfterAction(resp.data);
            },
            error: (err: any) => {
                this.actionLoading = false;
                const message = err?.error?.errors?.comprobante?.[0]
                    || (typeof err === 'string' ? err : 'No se pudo registrar el pago');
                this.utils.alertSwal({ title: 'Error', text: message, icon: 'error' });
            }
        });
        input.value = '';
    }

    /** Nombre del archivo de factura a partir de su URL pública. */
    invoiceFileName(p: PaymentDetail): string {
        if (!p.invoice_url) return '';
        const parts = p.invoice_url.split('/');
        return decodeURIComponent(parts[parts.length - 1] || '');
    }

    saveInvoiceDetails(): void {
        if (!this.detailPayment) return;
        this.providerService.updateInvoiceDetails(this.detailPayment.id, {
            invoice_number: this.invoiceNumberDraft || null,
            invoice_date: this.invoiceDateDraft || null,
        }).subscribe({
            next: (resp: any) => this.refreshAfterAction(resp.data),
            error: () => this.utils.alertSwal({ title: 'Error', text: 'No se pudo guardar la información de la factura', icon: 'error' }),
        });
    }

    deleteInvoiceFile(): void {
        if (!this.detailPayment) return;
        this.actionLoading = true;
        this.providerService.deleteInvoice(this.detailPayment.id).subscribe({
            next: (resp: any) => {
                this.actionLoading = false;
                this.refreshAfterAction(resp.data);
            },
            error: () => {
                this.actionLoading = false;
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo quitar la factura', icon: 'error' });
            }
        });
    }

    private toCard(r: any): MonthlyCard {
        const palette = this.avatarPalette[(r.provider_id || 0) % this.avatarPalette.length];
        return {
            ...r,
            avatarBg: palette.bg,
            avatarColor: palette.color,
            avatarInitial: (r.provider_name || '?').trim().charAt(0).toUpperCase(),
        };
    }

    statusLabel(status: number): string {
        if (status === 2) return 'Autorizado';
        if (status === 3) return 'Pagado';
        return 'Pendiente';
    }

    statusBadgeClass(status: number): string {
        if (status === 2) return 'monthly-badge monthly-badge--authorized';
        if (status === 3) return 'monthly-badge monthly-badge--paid';
        return 'monthly-badge monthly-badge--pending';
    }

    /**
     * Paso 1 = factura recibida (invoice_path cargado), paso 2 = autorización,
     * paso 3 = pago. El paso siguiente al último "done" se marca "current"
     * (confirmado con el diseño de Figma: nodo 5001:832 "SUCHAINT" muestra
     * el paso 1 en naranja/"current" para un pago recién Pendiente sin
     * factura todavía).
     */
    stepState(card: MonthlyCard, step: 1 | 2 | 3): 'done' | 'current' | 'pending' {
        const invoiceDone = card.has_invoice;
        const authDone = card.status >= 2;
        const paidDone = card.status >= 3;

        if (step === 1) return invoiceDone ? 'done' : 'current';
        if (step === 2) {
            if (authDone) return 'done';
            return invoiceDone ? 'current' : 'pending';
        }
        if (paidDone) return 'done';
        return authDone ? 'current' : 'pending';
    }

    step2Label(card: MonthlyCard): string {
        return card.status >= 2 ? 'Autorizado' : 'Autorización Pendiente';
    }

    step3Label(card: MonthlyCard): string {
        return card.status >= 3 ? 'Pagado' : 'Pago Pendiente';
    }

    /** Etiquetas de dos palabras van en dos líneas ("Factura" / "Recibida"). */
    twoLineLabel(text: string): string {
        return text.replace(' ', '<br>');
    }

    formatMoney(n: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);
    }

    formatDate(d: string | null): string {
        if (!d) return '—';
        const [y, m, day] = d.split('-');
        return `${day}/${m}/${y}`;
    }

    formatDateTime(d: string | null): string {
        if (!d) return '—';
        return this.formatDate(d.slice(0, 10));
    }

    exportCsv(): void {
        const header = ['Proveedor', 'Concepto', 'Monto', 'Fecha límite', 'Estatus'];
        const lines = [header.join(',')];
        this.cards.forEach(r => {
            const row = [r.provider_name, r.concept, r.amount, this.formatDate(r.due_date), this.statusLabel(r.status)];
            lines.push(row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        });
        const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pagos-proveedores-${this.period}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }
}
